import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { ApiMaestrosService } from '@core/api/services/maestros/api-maestros.service';
import { masterActions } from '@store/actions/master.actions';
import { MasterSocietyMapper } from '@core/domain/mappers/master/master-society.mapper';
import { MasterInstallationMapper } from '@core/domain/mappers/master/master-installation.mapper';
import { of } from 'rxjs';
import { MasterRoadTypesMapper } from '@core/domain/mappers/master/master-road-types.mapper';
import { userActions } from '@store/actions/user.actions';
import { HttpErrorResponse } from '@angular/common/http';
import { errorFromHttpErrorResponse } from '@core/services/request/util/error-from-http';
import { Store } from '@ngrx/store';
import { StoreState } from '@store/store.state';
import { masterSelectors } from '@store/selectors/master.selectors';
import { MasterSelfConsumptionsMapper } from '@core/domain/mappers/master/master-self-consumptions.mapper';

@Injectable()
export class MasterEffects {
  /**
   * Fetch master societies
   */
  fetchSocieties$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(masterActions.fetchSocieties, userActions.setUser),
      switchMap((action) =>
        this._apiService.fetchSocieties(action.type === userActions.setUser.type ? true : undefined).pipe(
          map((api) => api.data),
          map(MasterSocietyMapper.modelFromApi),
          map((data) => masterActions.setSocieties({ data })),
          catchError((err: HttpErrorResponse) => of(masterActions.errorSocieties({ error: errorFromHttpErrorResponse(err) })))
        )
      )
    );
  });
  /**
   * Fetch master installations
   */
  fetchInstallations$ = createEffect(() =>
    this._actions$.pipe(
      ofType(masterActions.fetchInstallations, userActions.setUser),
      switchMap((action) =>
        this._apiService.fetchInstallations(action.type === userActions.setUser.type ? true : undefined).pipe(
          map((api) => api.data),
          map(MasterInstallationMapper.modelFromApi),
          map((data) => masterActions.setInstallations({ data })),
          catchError((err: HttpErrorResponse) => of(masterActions.errorInstallations({ error: errorFromHttpErrorResponse(err) })))
        )
      )
    )
  );
  /**
   * Fetch master road types
   */
  fetchRoadTypes$ = createEffect(() =>
    this._actions$.pipe(
      ofType(masterActions.fetchRoadTypes),
      filter(() => {
        const roadTypes = this._store.selectSignal(masterSelectors.roadTypes)();

        return roadTypes == null || roadTypes.length === 0;
      }),
      switchMap(() =>
        this._apiService.fetchRoadTypes().pipe(
          map((api) => MasterRoadTypesMapper.requestFromApiToModel(api).data),
          map((data) => masterActions.setRoadTypes({ data })),
          catchError((err: HttpErrorResponse) => of(masterActions.errorRoadTypes({ error: errorFromHttpErrorResponse(err) })))
        )
      )
    )
  );

  /**
   * Fetch master self consumptions
   */
  fetchSelfConsumptions$ = createEffect(() =>
    this._actions$.pipe(
      ofType(masterActions.fetchSelfConsumptions),
      filter(() => {
        const selfConsumptions = this._store.selectSignal(masterSelectors.selfConsumptions)();
        return selfConsumptions == null || selfConsumptions.length === 0;
      }),
      switchMap(() =>
        this._apiService.fetchSelfConsumptions().pipe(
          map((api) => MasterSelfConsumptionsMapper.requestFromApiToModel(api).data),
          map((data) => masterActions.setSelfConsumptions({ data })),
          catchError((err: HttpErrorResponse) => of(masterActions.errorSelfConsumptions({ error: errorFromHttpErrorResponse(err) })))
        )
      )
    )
  );

  /**
   * Fetch master cups
   */
  fetchCups$ = createEffect(() =>
    this._actions$.pipe(
      ofType(masterActions.fetchCups),
      filter(() => {
        const cups = this._store.selectSignal(masterSelectors.cups)();
        return cups == null || cups.length === 0;
      }),
      switchMap(() =>
        this._apiService.fetchCups().pipe(
          map((api) => MasterSelfConsumptionsMapper.requestFromApiToModel(api).data),
          map((data) => masterActions.setCups({ data })),
          catchError((err: HttpErrorResponse) => of(masterActions.errorCups({ error: errorFromHttpErrorResponse(err) })))
        )
      )
    )
  );

  /**
   * Fetch master subsection type
   */
  fetchSubsectionType$ = createEffect(() =>
    this._actions$.pipe(
      ofType(masterActions.fetchSubsectionType),
      filter(() => {
        const subsectionType = this._store.selectSignal(masterSelectors.subsectionType)();
        return subsectionType == null || subsectionType.length === 0;
      }),
      switchMap(() =>
        this._apiService.fetchSubsectionType().pipe(
          map((api) => MasterSelfConsumptionsMapper.requestFromApiToModel(api).data),
          map((data) => masterActions.setSubsectionType({ data })),
          catchError((err: HttpErrorResponse) => of(masterActions.errorSubsectionType({ error: errorFromHttpErrorResponse(err) })))
        )
      )
    )
  );

  /**
   * Fetch master installation type
   */
  fetchInstallationType$ = createEffect(() =>
    this._actions$.pipe(
      ofType(masterActions.fetchInstallationType),
      filter(() => {
        const installationType = this._store.selectSignal(masterSelectors.installationType)();
        return installationType == null || installationType.length === 0;
      }),
      switchMap(() =>
        this._apiService.fetchInstallationType().pipe(
          map((api) => MasterSelfConsumptionsMapper.requestFromApiToModel(api).data),
          map((data) => masterActions.setInstallationType({ data })),
          catchError((err: HttpErrorResponse) => of(masterActions.errorInstallationType({ error: errorFromHttpErrorResponse(err) })))
        )
      )
    )
  );

  constructor(
    private _actions$: Actions,
    private _apiService: ApiMaestrosService,
    private _store: Store<StoreState>
  ) {}
}
